/**
 * Passive Events Utility
 * Improves performance by making touch events passive by default
 */

// List of events that should be passive for better performance
const PASSIVE_EVENTS = [
  'touchstart',
  'touchmove', 
  'touchend',
  'wheel',
  'mousewheel',
  'DOMMouseScroll'
];

/**
 * Check if an event should be passive
 * @param {string} eventType - The event type to check
 * @returns {boolean} - Whether the event should be passive
 */
export function shouldBePassive(eventType) {
  return PASSIVE_EVENTS.includes(eventType);
}

/**
 * Add passive event listener
 * @param {Element} element - The element to add listener to
 * @param {string} event - The event type
 * @param {Function} handler - The event handler
 * @param {Object} options - Additional options
 */
export function addPassiveEventListener(element, event, handler, options = {}) {
  const passiveOptions = {
    ...options,
    passive: shouldBePassive(event) || options.passive !== false
  };
  
  element.addEventListener(event, handler, passiveOptions);
}

/**
 * Remove event listener
 * @param {Element} element - The element to remove listener from
 * @param {string} event - The event type
 * @param {Function} handler - The event handler
 * @param {Object} options - Additional options
 */
export function removePassiveEventListener(element, event, handler, options = {}) {
  const passiveOptions = {
    ...options,
    passive: shouldBePassive(event) || options.passive !== false
  };
  
  element.removeEventListener(event, handler, passiveOptions);
}

/**
 * Vue directive for passive touch events
 */
export const passiveTouchDirective = {
  mounted(el, binding) {
    const events = ['touchstart', 'touchmove', 'touchend'];
    events.forEach(event => {
      addPassiveEventListener(el, event, binding.value);
    });
  },
  
  unmounted(el, binding) {
    const events = ['touchstart', 'touchmove', 'touchend'];
    events.forEach(event => {
      removePassiveEventListener(el, event, binding.value);
    });
  }
};

/**
 * Initialize passive events globally
 */
export function initPassiveEvents() {
  // Override addEventListener to use passive by default
  const originalAddEventListener = EventTarget.prototype.addEventListener;
  EventTarget.prototype.addEventListener = function(type, listener, options) {
    if (typeof options === 'boolean') {
      options = { capture: options };
    }
    
    // Make performance-critical events passive by default
    if (shouldBePassive(type)) {
      options = { ...options, passive: true };
    }
    
    return originalAddEventListener.call(this, type, listener, options);
  };
  
  // Override removeEventListener to match
  const originalRemoveEventListener = EventTarget.prototype.removeEventListener;
  EventTarget.prototype.removeEventListener = function(type, listener, options) {
    if (typeof options === 'boolean') {
      options = { capture: options };
    }
    
    if (shouldBePassive(type)) {
      options = { ...options, passive: true };
    }
    
    return originalRemoveEventListener.call(this, type, listener, options);
  };
}
